import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import HPageContainer from "../../../hcomponents/HPageContainer";
import HHeader from "../../../hcomponents/HHeader";
import { Tabs, TabsList, TabsTrigger } from "../../../shadcn/ui/tabs";
import billingService from "../../../services/billingService";
import * as localService from "../../../services/localService";
import { FeatureAccess } from "../../../utils/featureAccessControl";
import HPagination from "../../../hcomponents/HPagination";
import HActionButton from "../../../hcomponents/HActionButton";
import {
  Plus,
  Search,
  Calendar,
  FileText,
  CheckCircle,
  Clock,
  XCircle,
  ArrowUpRight,
  ArrowDownLeft,
} from "lucide-react";
import { Input } from "../../../shadcn/ui/input";
import moment from "moment-timezone";
import { useUserCurrency } from "../../../hooks/useUserCurrency";
import logger from "../../../utils/logger";


const HostInvoiceList = () => {
  const history = useHistory();
  const location = useLocation();

  // Initialize state directly from URL params to prevent a double-fetch:
  // Previously, state was initialized with hardcoded defaults and then
  // overwritten by a useEffect reading the URL — causing two fetches on load.
  const getInitialParams = () => {
    const params = new URLSearchParams(location.search);
    return {
      page: parseInt(params.get("page")) || 1,
      search: params.get("q") || "",
      tab: params.get("tab") || "invoices",
      statusFilter: params.get("status") || "ALL",
      orderBy: params.get("orderBy") || "createdAt:desc",
    };
  };

  const initialParams = getInitialParams();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(initialParams.page);
  const [limit, setLimit] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState(initialParams.search);
  const [debouncedSearch, setDebouncedSearch] = useState(initialParams.search);
  const [tab, setTab] = useState(initialParams.tab);
  const [statusFilter, setStatusFilter] = useState(initialParams.statusFilter);
  const [orderBy, setOrderBy] = useState(initialParams.orderBy);

  const currentUser = localService.getUser?.() || null;
  const canManageInvoices = FeatureAccess.canManageInvoices(currentUser);
  const userCurrencyCode = useUserCurrency();

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch Data
  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const orgId = currentUser?.organization?.id || currentUser?.organizationId;

        if (!orgId) {
          logger.error("No organization ID found");
          setItems([]);
          setLoading(false);
          return;
        }

        const baseParams = {
          providerId: orgId,
          page,
          limit,
          orderBy,
        };

        // Add search query if present
        if (debouncedSearch && debouncedSearch.trim().length > 0) {
          baseParams.q = debouncedSearch.trim();
        }

        // Add status filter if not ALL
        if (statusFilter && statusFilter !== "ALL") {
          baseParams.status = statusFilter;
        }

        // Invoices tab: include relations for richer data
        if (tab === "invoices") {
          baseParams.includeRelations = true;
        }

        const response =
          tab === "invoices"
            ? await billingService.getInvoices(baseParams)
            : await billingService.getTransactions({
                organizationId: orgId,
                page,
                limit,
                orderBy,
                ...(debouncedSearch ? { q: debouncedSearch } : {}),
              });

        if (response?.status === 200) {
          const rows = response.data?.data || response.data || [];
          setItems(Array.isArray(rows) ? rows : []);
          const pagination = response.data?.pagination || {};
          setTotalCount(
            pagination?.totalCount || response.data?.total || response.data?.totalCount || rows.length || 0
          );
          setLimit(pagination?.limit || limit);
        } else {
          setItems([]);
          setTotalCount(0);
        }
      } catch (err) {
        logger.error("Error loading list:", err);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, debouncedSearch, canManageInvoices, tab, statusFilter, orderBy]);

  const updateUrl = newParams => {
    const params = new URLSearchParams(location.search);
    Object.keys(newParams).forEach(key => {
      if (newParams[key] === null || newParams[key] === undefined) {
        params.delete(key);
      } else {
        params.set(key, newParams[key]);
      }
    });
    history.replace({ search: params.toString() });
  };

  const handlePageChange = newPage => {
    // HPagination logic: if it returns 0-based, convert to 1-based, or vice versa?
    // Looking at HostSpaces usage:
    // const getInitialPage = () => ... max(0, parseInt(pageParam) - 1) ... so it uses 0-based internal state?
    // But API uses 1-based usually.
    // Let's assume onPageChange passes the new page number to display (1-based) or index (0-based).
    // HostSpaces: handlePageChange = newPage => { setCurrentPage(newPage); updateUrlParams(newPage); }
    // UpdateUrlParams: if (page === 0) delete page else set page + 1. So HostSpaces uses 0-based internally.
    // Here we use 1-based for API.
    // Let's assume onPageChange gives 0-based index if it matches typical Paginator, or 1-based.
    // I'll check HPagination again if I can... or safer:
    // Actually, HostSpaces uses `pageData.totalPages` and `currentPage`.
    // Let's assume standard behavior: 0-based index from component.
    setPage(newPage + 1);
    updateUrl({ page: newPage + 1 });
  };

  const handleTabChange = val => {
    setTab(val);
    setPage(1);
    setSearch("");
    setDebouncedSearch("");
    setStatusFilter("ALL");
    updateUrl({ tab: val, page: 1, q: "", status: "ALL" });
  };

  const handleSearchChange = e => {
    setSearch(e.target.value);
    // Debounce handled by useEffect
    updateUrl({ q: e.target.value, page: 1 });
  };

  const handleViewInvoice = invoiceId => {
    history.push(`/host/invoice/${invoiceId}`);
  };

  const formatCurrency = amount =>
    `${userCurrencyCode || "KES"} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const renderStatusBadge = status => {
    const s = (status || "").toUpperCase();
    let colorClass = "bg-gray-100 text-gray-600";
    let icon = <Clock className="w-3 h-3 mr-1" />;

    if (s === "PAID" || s === "COMPLETED" || s === "SUCCESS") {
      colorClass = "bg-green-100 text-green-700";
      icon = <CheckCircle className="w-3 h-3 mr-1" />;
    } else if (s === "PENDING") {
      colorClass = "bg-yellow-100 text-yellow-800";
      icon = <Clock className="w-3 h-3 mr-1" />;
    } else if (s === "CANCELLED" || s === "FAILED") {
      colorClass = "bg-red-100 text-red-700";
      icon = <XCircle className="w-3 h-3 mr-1" />;
    }

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {icon}
        {s}
      </span>
    );
  };

  const renderInvoiceItem = invoice => {
    const created = invoice.createdAt || invoice.serviceDate;
    const amount = parseFloat(invoice.amount || 0);
    const status = invoice.status || "";

    return (
      <div
        key={invoice.id}
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors bg-white shadow-sm"
        onClick={() => handleViewInvoice(invoice.id)}
      >
        <div className="mb-2 sm:mb-0">
          <div className="text-sm text-gray-500 flex items-center gap-2">
            <Calendar className="w-3 h-3" />
            {created ? new Date(created).toLocaleDateString() : "-"}
          </div>
          <div className="font-medium text-gray-900 mt-1">{invoice.referenceCode || `INV-${invoice.id}`}</div>
          <div className="text-sm text-gray-600 truncate max-w-md">{invoice.description || "Invoice"}</div>
          <div className="text-xs text-gray-500 mt-1">
            Client: {invoice.organization?.name || invoice.clientName || "-"}
          </div>
        </div>

        <div className="flex flex-col sm:items-end gap-2 w-full sm:w-auto">
          <div className="font-bold text-gray-900 text-lg">{formatCurrency(amount)}</div>
          <div className="flex items-center gap-2">{renderStatusBadge(status)}</div>
        </div>
      </div>
    );
  };

  const renderTransactionItem = transaction => {
    const status = transaction.status || "";
    const type = transaction.type || "";
    const isCredit = type === "CREDIT" || status === "PAID" || status === "COMPLETED" || status === "SUCCESS";
    const amount = parseFloat(transaction.amount || transaction.value || 0);
    const date = transaction.createdAt || transaction.date;

    return (
      <div
        key={transaction.id}
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors bg-white shadow-sm"
      >
        <div className="mb-2 sm:mb-0">
          <div className="text-sm text-gray-500 flex items-center gap-2">
            <Calendar className="w-3 h-3" />
            {date ? new Date(date).toLocaleDateString() : "-"}
          </div>
          <div className="font-medium text-gray-900 mt-1">
            {transaction.description || transaction.narration || "Transaction"}
          </div>
          <div className="text-sm text-gray-600">
            {transaction.company || transaction.organization?.name || "-"}
            <span className="text-gray-400 mx-2">|</span>
            <span className="text-gray-500">
              Ref: {transaction.reference || transaction.invoiceReference || `TX-${transaction.id}`}
            </span>
          </div>
        </div>
        <div className="flex flex-col sm:items-end gap-1">
          <div className={`font-bold text-lg flex items-center ${isCredit ? "text-green-600" : "text-red-600"}`}>
            {isCredit ? "+" : "-"} {formatCurrency(amount)}
          </div>
          <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">
            {isCredit ? "Credit" : "Debit"}
          </div>
        </div>
      </div>
    );
  };

  return (
    <HPageContainer>
      <HHeader
        rightButton={
          canManageInvoices ? (
            <HActionButton
              icon={<Plus className="w-4 h-4" />}
              label="Create Invoice"
              onClick={() => history.push("/host/add-invoice")}
            />
          ) : null
        }
      />
      <div className="overflow-auto flex-1 p-4 sm:p-6 md:p-8 pb-20">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div className="flex flex-1 w-full md:w-auto gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
              <Input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={handleSearchChange}
                className="!pl-10 w-full"
              />
            </div>
            {tab === "invoices" && (
              <select
                value={statusFilter}
                onChange={e => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                  updateUrl({ status: e.target.value, page: 1 });
                }}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="ALL">All Status</option>
                <option value="PAID">Paid</option>
                <option value="PENDING">Pending</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            )}
          </div>
        </div>

        {loading ? (
          <div className="space-y-4 animate-pulse">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-100 rounded-lg"></div>
            ))}
          </div>
        ) : items.length > 0 ? (
          <div className="space-y-4 mb-6">
            {tab === "invoices" ? items.map(renderInvoiceItem) : items.map(renderTransactionItem)}
          </div>
        ) : (
          <div className="py-12 text-center text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
            <FileText className="mx-auto h-12 w-12 text-gray-300 mb-3" />
            <h3 className="text-lg font-medium text-gray-900">No {tab} found</h3>
            <p className="max-w-sm mx-auto mt-1">
              Try adjusting your search or filters to find what you're looking for.
            </p>
            {tab === "invoices" && canManageInvoices && (
              <button
                onClick={() => history.push("/host/add-invoice")}
                className="mt-4 text-blue-600 font-medium hover:underline"
              >
                Create your first invoice
              </button>
            )}
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="mt-6 flex justify-center">
            <HPagination
              currentPage={page - 1} // HPagination expects 0-based index based on HostSpaces
              totalPages={Math.ceil(totalCount / limit)}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </HPageContainer>
  );
};

export default HostInvoiceList;
